import { SessionExpiredError } from '@/feature/auth/errors/SessionExpiredError';

/**
 * Token refresh middleware — intercepts 401/400/403 errors and either:
 *   1. refreshes the third-party X-Api-Token and retries (refresh path), or
 *   2. detects user-session expiry and triggers logout via `onLogout`.
 *
 * Session-expiry detection (DEVUL-7821): when the user's session is gone,
 * affected callsites used to surface a generic error modal. We now throw a
 * `SessionExpiredError` sentinel so `errorModal/handleError` can suppress
 * the modal and `onLogout` can route to /login. The `isLoggedIn` getter is
 * called per-request (NOT captured at construction) — the middleware is
 * built once at app boot when isLoggedIn is false, so a snapshot would
 * never see the user log in.
 */
export function createTokenRefreshMiddleware({
  refreshFn,
  onLogout,
  isLoggedIn,
  isMobileApp,
  getPrivateKey,
  getPartnerClientId,
  baseURL
}) {
  let refreshPromise = null;

  // The session-expiry triggers below all funnel through this helper. It
  // ALWAYS evaluates isLoggedIn() at call time — never store the boolean.
  const triggerSessionExpired = error => {
    if (typeof isLoggedIn === 'function' && isLoggedIn()) {
      onLogout();
      return new SessionExpiredError(`Session expired (HTTP ${error.status})`, error);
    }
    return error;
  };

  // Endpoints where a 401 is *expected* and must never be interpreted as
  // session-expiry. /v2/authorization/logout is the canonical case — calling
  // it with an already-dead session returns 401 and would otherwise recurse
  // back into auth/logout via onLogout (see notes/session-expiry-research.md
  // Risk #9).
  const expectedFailure401 = ['/v2/authorization/logout'];

  // Paths where the user may *not yet* be logged in and a 401 is part of the
  // normal flow (login submission, MFA validation). The isLoggedIn() gate in
  // triggerSessionExpired handles these naturally — listed here for clarity
  // alongside the historical skip list.
  const skipped = [
    '/v2/token',
    '/v2/authorization',
    '/v3/fe/web-user',
    // Also in expectedFailure401 for the 401 case (handled earlier);
    // listed here so non-401 status codes from logout also skip refresh.
    '/v2/authorization/logout',
    '/v2/authorization/validate-sms-code',
    '/v3/fe/web-users/change-password',
    // Account deletion — a wrong password is returned as a non-401 (422)
    // with an error message, so skip refresh and propagate raw to the delete
    // workflow's catch instead of refreshing the token. A genuine 401 here
    // still means the session expired and correctly triggers logout.
    '/v3/web-users',
    '/v3/fe/questions',
    '/v3/fe/appointments',
    '/v3/fe/web-users/resend-2fa-code',
    '/v2/authorization/third-party',
    '/v3/fe/web-user/benefit/authorization'
  ];

  const skippedSubstr = ['/v3/fe/questions/', '/v3/fe/appointments/'];

  return next =>
    async (url, init = {}) => {
      try {
        return await next(url, init);
      } catch (error) {
        // If error is not HttpError-like (no .status), propagate
        if (!error || typeof error.status !== 'number') {
          throw error;
        }

        const { status } = error;

        // Extract path from URL — url is already a full URL from httpClient
        // Strip the baseURL pathname prefix (e.g. '/api' → '/v2/token')
        const parsedUrl = new URL(url);
        const basePath = new URL(baseURL).pathname.replace(/\/$/, '');
        const path = parsedUrl.pathname.slice(basePath.length);

        // /v2/authorization/logout 401 is expected when the server-side
        // session is already dead — propagate so auth/logout's try/catch
        // can swallow it. Never call onLogout here (would recurse).
        if (expectedFailure401.includes(path) && status === 401) {
          throw error;
        }

        // Special logout triggers — endpoints where a 401 means the session
        // is definitely gone (the refresh path itself failing, or fetching
        // the authenticated user record). Gated on isLoggedIn so login-form
        // 401s on /v2/token aren't treated as session-expiry.
        if (
          ((path === '/v2/token' || path === '/v2/authorization/refresh-access-token') &&
            status === 401) ||
          (path === '/v3/fe/web-user' && status === 401)
        ) {
          throw triggerSessionExpired(error);
        }

        // Whitelist — skip refresh. If the user IS logged in and the response
        // is a 401, treat it as session-expiry; otherwise propagate the raw
        // HttpError so per-call handlers (login form, MFA challenge) can react.
        if (skipped.includes(path) || skippedSubstr.some(substr => path.includes(substr))) {
          if (status === 401) {
            throw triggerSessionExpired(error);
          }
          throw error;
        }

        // Check if this is a refresh-triggering condition
        const shouldRefresh =
          (status === 400 && error.data?.message === 'Missing required parameter in header') ||
          status === 401 ||
          (status === 403 && (!error.data?.error || error.data?.error === 'Invalid api token'));

        if (!shouldRefresh) {
          throw error;
        }

        // Prevent infinite loop — don't retry if already a retry
        if (init.__isRetryRequest) {
          throw error;
        }

        // Deduplicated refresh
        if (!refreshPromise) {
          const isMobile = isMobileApp();
          const partnerHash = getPrivateKey(isMobile);
          const partnerClientId = getPartnerClientId(isMobile);

          refreshPromise = refreshFn({
            partnerHash,
            partnerId: 0,
            partnerClientId
          }).finally(() => {
            refreshPromise = null;
          });
        }

        try {
          await refreshPromise;
        } catch (refreshError) {
          // Refresh failed — the user-session refresh-access-token call inside
          // refreshFn is what actually rejected. Treat as session-expiry.
          throw triggerSessionExpired(refreshError instanceof Error ? refreshError : error);
        }

        // Retry original request. If the retry itself comes back 401, the
        // X-Api-Token was successfully refreshed but the *user session* is
        // dead — treat as session-expiry (otherwise the rejection bubbles
        // out unhandled and surfaces the generic error modal, the exact
        // pentest finding we're closing).
        try {
          return await next(url, { ...init, __isRetryRequest: true });
        } catch (retryError) {
          if (retryError?.status === 401) {
            throw triggerSessionExpired(retryError);
          }
          throw retryError;
        }
      }
    };
}
